
// SelectedShortingRow.tsx
import React from "react";
import clsx from "clsx";
import DragIcon from "app/components/Icons/DragIcon";
import { CloseIcon } from "app/components/Icons";
import FieldSelect from "./FieldSelect";
import OrderSelect from "./OrderSelect";
import { sortOrder } from "app/types";

interface RowProps {
  index: number;
  rule: { fieldKey: string | null; order: sortOrder };
  fieldOptions: { key: string; label: string }[];
  onDragStart: (i: number) => void;
  onDragOver: (e: React.DragEvent, i: number) => void;
  onDrop: (i: number) => void;
  onUpdateField: (index: number, fieldKey: string | null) => void;
  onUpdateOrder: (index: number, order: sortOrder) => void;
  onRemove: (index: number) => void;
}

const SelectedShortingRow: React.FC<RowProps> = ({
  index,
  rule,
  fieldOptions,
  onDragStart,
  onDragOver,
  onDrop,
  onUpdateField,
  onUpdateOrder,
  onRemove,
}) => {
  const isPlaceholder = rule.fieldKey === null;

  return (
    <div
      className={clsx(
        "grid grid-cols-[24px_1fr_140px_32px] gap-2 items-center px-4 py-3"
      )}
      draggable={!isPlaceholder}
      onDragStart={!isPlaceholder ? () => onDragStart(index) : undefined}
      onDragOver={!isPlaceholder ? (e) => onDragOver(e, index) : undefined}
      onDrop={!isPlaceholder ? () => onDrop(index) : undefined}
    >
      {/* drag handle */}
      <div
        className={clsx(
          "flex items-center",
          isPlaceholder ? "opacity-40 cursor-not-allowed" : "cursor-grab"
        )}
        aria-disabled={isPlaceholder}
        title={
          isPlaceholder ? "Select a field to enable drag" : "Drag to reorder"
        }
      >
        <DragIcon />
      </div>

      {/* field select */}
      <FieldSelect
        options={fieldOptions}
        value={rule.fieldKey ?? null}
        onUpdate={(val) => onUpdateField(index, val)}
      />

      {/* order select */}
      <OrderSelect
        value={rule.order}
        onUpdate={(val) => onUpdateOrder(index, val)}
      />

      {/* remove rule */}
      <button
        aria-label="remove sort rule"
        onClick={() => onRemove(index)}
        className={clsx(
          "rounded-md p-1",
          isPlaceholder
            ? "text-gray-300 opacity-50 cursor-not-allowed"
            : "text-gray-400 hover:text-gray-100"
        )}
        disabled={isPlaceholder}
      >
        <CloseIcon />
      </button>
    </div>
  );
};

export default SelectedShortingRow
